import { asDate, formatDateTime, formatDuration } from './formatters';
import { normalizeOrderEvent, normalizeOrderEventType } from './events';

const EVENT_STAGE_MAP = {
  ORDER_CREATED: 'new',
  STORE_ASSIGNED: 'awaiting_store',
  ROUTED_TO_STORE: 'awaiting_store',
  STORE_ACCEPTED: 'awaiting_store',
  STORE_CONFIRMED: 'awaiting_store',
  SWAP_REQUESTED: 'issue',
  SWAP_APPROVED: 'awaiting_store',
  PACKING_STARTED: 'awaiting_store',
  PACKING_PROOF: 'ready_for_driver',
  READY_FOR_DRIVER: 'ready_for_driver',
  DRIVER_REQUESTED: 'ready_for_driver',
  DRIVER_ASSIGNED: 'ready_for_driver',
  DRIVER_ACCEPTED: 'out_for_delivery',
  COLLECTED: 'ready_for_driver',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  RETURNED: 'returned',
  CANCELLED: 'cancelled',
  ISSUE_FLAGGED: 'issue',
};

const EVENT_SLOT_MAP = {
  ORDER_CREATED: 'createdAt',
  STORE_ASSIGNED: 'routedAt',
  ROUTED_TO_STORE: 'routedAt',
  STORE_ACCEPTED: 'acceptedAt',
  STORE_CONFIRMED: 'acceptedAt',
  SWAP_REQUESTED: 'issueAt',
  SWAP_APPROVED: 'acceptedAt',
  PACKING_STARTED: 'packingAt',
  PACKING_PROOF: 'readyAt',
  READY_FOR_DRIVER: 'readyAt',
  DRIVER_REQUESTED: 'driverRequestedAt',
  DRIVER_ASSIGNED: 'assignedAt',
  DRIVER_ACCEPTED: 'outAt',
  COLLECTED: 'collectedAt',
  OUT_FOR_DELIVERY: 'outAt',
  DELIVERED: 'deliveredAt',
  RETURNED: 'returnedAt',
  CANCELLED: 'cancelledAt',
  ISSUE_FLAGGED: 'issueAt',
  ROUTE_OVERWRITTEN: 'overwrittenAt',
};

function eventDate(event) {
  return asDate(event?.createdAt || event?.at || event?.timestamp || event?.clientAt || null);
}

function sortAsc(events = []) {
  return [...events].sort((a, b) => (eventDate(a)?.getTime?.() || 0) - (eventDate(b)?.getTime?.() || 0));
}

function secondsBetween(a, b) {
  const start = asDate(a);
  const end = asDate(b);
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
}

export function getOrderStatusFromEvents(events = [], fallbackStage = 'new') {
  let stage = fallbackStage || 'new';
  sortAsc(events).forEach((event) => {
    const type = normalizeOrderEventType(event?.type, event);
    const next = EVENT_STAGE_MAP[type];
    if (next) stage = next;
  });
  return stage;
}

export function buildTimelineState(events = [], raw = {}) {
  const timings = raw?.timings || {};
  const sorted = sortAsc(events).map(normalizeOrderEvent);
  const slots = {
    createdAt: asDate(raw?.createdAt || raw?.placedAt || timings.createdAt || timings.placedAt || null),
    routedAt: asDate(timings.sentToStoreAt || timings.routedAt || null),
    acceptedAt: asDate(timings.storeAcceptedAt || timings.storeConfirmedAt || null),
    packingAt: asDate(timings.packingStartedAt || null),
    readyAt: asDate(timings.readyForDriverAt || timings.packedAt || timings.readyAt || null),
    driverRequestedAt: asDate(timings.driverRequestedAt || null),
    assignedAt: asDate(timings.driverAssignedAt || null),
    collectedAt: asDate(timings.collectedAt || timings.pickedUpAt || null),
    outAt: asDate(timings.outForDeliveryAt || timings.driverAcceptedAt || null),
    deliveredAt: asDate(timings.deliveredAt || timings.completedAt || null),
    returnedAt: asDate(timings.returnedAt || null),
    cancelledAt: asDate(timings.cancelledAt || null),
    issueAt: asDate(timings.swapApprovalStartedAt || timings.unavailableMarkedAt || null),
    overwrittenAt: asDate(raw?.routing?.overwrite?.at || null),
  };

  sorted.forEach((event) => {
    const slot = EVENT_SLOT_MAP[event?.type];
    const dt = eventDate(event);
    if (slot && dt) slots[slot] = dt;
  });

  const currentStage = getOrderStatusFromEvents(sorted, raw?.stage || raw?.status || 'new');
  return {
    currentStage,
    ...slots,
    onlineReviewSeconds: secondsBetween(slots.createdAt, slots.routedAt),
    storeWaitSeconds: secondsBetween(slots.routedAt, slots.acceptedAt || slots.packingAt || slots.readyAt),
    packingSeconds: secondsBetween(slots.packingAt || slots.acceptedAt, slots.readyAt),
    approvalDelaySeconds: secondsBetween(slots.issueAt, slots.acceptedAt || slots.readyAt),
    readyForDriverSeconds: secondsBetween(slots.readyAt, slots.outAt || slots.collectedAt),
    driverWaitSeconds: secondsBetween(slots.driverRequestedAt || slots.assignedAt, slots.outAt || slots.collectedAt),
    deliveryRunSeconds: secondsBetween(slots.outAt || slots.collectedAt, slots.deliveredAt),
    lifecycleSeconds: secondsBetween(slots.createdAt, slots.deliveredAt || slots.cancelledAt || new Date()),
  };
}

function fallbackDate(row = {}) {
  const text = row?.at || row?.timestamp || row?.createdAt || '';
  return asDate(text) || null;
}

function normalizeFallbackRow(row = {}, index = 0) {
  const type = normalizeOrderEventType(row.type || 'ORDER_UPDATE', row);
  return {
    id: row.id || `fallback-${index}-${String(row.text || row.message || type).slice(0, 18)}`,
    type,
    title: row.title || row.text || row.message || type,
    text: row.text || row.message || row.title || type,
    actorLabel: row.actorLabel || row.actor || '',
    at: row.at || formatDateTime(fallbackDate(row)),
    durationFromPrevious: '',
    meta: row.meta || {},
  };
}

export function buildTimelineRows(events = [], fallbackRows = []) {
  const sourceEvents = Array.isArray(events) && events.length ? events : [];
  if (!sourceEvents.length) return (fallbackRows || []).map(normalizeFallbackRow);

  const sorted = sortAsc(sourceEvents).map(normalizeOrderEvent);
  return sorted.map((event, index) => {
    const currentAt = eventDate(event);
    const previousAt = index > 0 ? eventDate(sorted[index - 1]) : null;
    const delta = previousAt && currentAt ? secondsBetween(previousAt, currentAt) : 0;
    return {
      id: event.id || `${event.type}-${currentAt?.getTime?.() || index}`,
      type: event.type,
      title: event.title,
      text: event.message || event.title || event.type,
      actorLabel: event.actorLabel,
      at: formatDateTime(currentAt),
      durationFromPrevious: index > 0 && delta ? formatDuration(delta) : '',
      meta: event.meta || {},
      photoUrl: event.photoUrl || event.meta?.photoUrl || '',
      note: event.note || event.meta?.note || '',
    };
  }).reverse();
}
